import { z } from 'zod';

export const ScenarioSlugSchema = z.string().min(3).max(64).regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/, 'Use a lowercase slug with letters, numbers and single hyphens.').refine((value) => value !== 'new', 'The scenario ID "new" is reserved for the create screen. Choose another ID.');
const shortText = z.string().trim().min(1).max(180);
export const ScenarioDraftInputSchema = z.object({
  titleZh: z.string().trim().min(1).max(120),
  titleEn: z.string().trim().min(1).max(160),
  descriptionZh: z.string().trim().min(1).max(800),
  category: z.enum(['academic', 'social', 'workplace']),
  difficulty: z.enum(['入門', '中階', '進階']),
  durationMinutes: z.number().int().min(1).max(60),
  supportedModes: z.array(z.union([z.literal(1), z.literal(2), z.literal(3)])).min(1).max(3).refine((values) => new Set(values).size === values.length, 'Discussion modes must be unique.'),
  objectives: z.array(shortText).min(1).max(6),
  vocabulary: z.array(z.string().trim().min(1).max(40)).min(1).max(20),
}).strict();
export type ScenarioDraftInput = z.infer<typeof ScenarioDraftInputSchema>;
export const ScenarioContentSchema = ScenarioDraftInputSchema.extend({ id: ScenarioSlugSchema }).strict();
export type ScenarioContent = z.infer<typeof ScenarioContentSchema>;
export const ManagedScenarioSchema = z.object({
  id: ScenarioSlugSchema,
  status: z.enum(['draft', 'published', 'archived']),
  draft: ScenarioContentSchema,
  published: ScenarioContentSchema.optional(),
  version: z.number().int().positive(),
  publishedVersion: z.number().int().positive().optional(),
  createdAt: z.string().datetime(), updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime().optional(), archivedAt: z.string().datetime().optional(),
  hasUnpublishedChanges: z.boolean(),
}).strict().superRefine((record, context) => {
  if (record.draft.id !== record.id || (record.published && record.published.id !== record.id)) context.addIssue({ code: z.ZodIssueCode.custom, message: 'Scenario IDs must match.' });
  if (record.status === 'published' && (!record.published || !record.publishedVersion)) context.addIssue({ code: z.ZodIssueCode.custom, message: 'Published content is required.' });
  if (record.publishedVersion && record.publishedVersion > record.version) context.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid published version.' });
});
export type ManagedScenario = z.infer<typeof ManagedScenarioSchema>;
export const ScenarioManagementListSchema = z.object({ scenarios: z.array(ManagedScenarioSchema) });
export type ScenarioManagementList = z.infer<typeof ScenarioManagementListSchema>;
